import { useCallback, useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Document, Page, pdfjs } from "react-pdf";
import PopUp from "src/components/common/PopUp";

// pdf.js worker is copied into public/ (see public/pdf.worker.min.mjs) so it is
// served from the same origin and its version matches the installed pdfjs-dist.
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

const GUIDELINES_PDF = `${process.env.PUBLIC_URL}/guidelines-revised-kml-upload.pdf`;

// How close (px) to the end counts as "reached the bottom".
const BOTTOM_THRESHOLD = 24;

type AckGuidelinesModalProps = {
  open: boolean;
  onClose: () => void;
  onAcknowledge: () => void;
};

/**
 * One-time acknowledgement gate shown before the New KML upload modal. The user
 * must scroll through the entire guidelines PDF (rendered page-by-page in a
 * scroll container) before the "Acknowledged" action is enabled.
 */
const AckGuidelinesModal = ({
  open,
  onClose,
  onAcknowledge,
}: AckGuidelinesModalProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const [numPages, setNumPages] = useState(0);
  const [renderedCount, setRenderedCount] = useState(0);
  const [pageWidth, setPageWidth] = useState(760);
  const [reachedBottom, setReachedBottom] = useState(false);
  const [loadError, setLoadError] = useState(false);

  // Reset the gate every time the modal is opened.
  useEffect(() => {
    if (open) {
      setNumPages(0);
      setRenderedCount(0);
      setReachedBottom(false);
      setLoadError(false);
    }
  }, [open]);

  const measureWidth = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // clientWidth excludes the scrollbar; subtract the container padding (16px
    // each side) plus a little breathing room for the page's drop shadow.
    const available = el.clientWidth - 40;
    if (available > 0) setPageWidth(available);
  }, []);

  useEffect(() => {
    if (!open) return;
    // Measure once mounted, and keep pages sized correctly on window resize.
    const id = window.setTimeout(measureWidth, 0);
    window.addEventListener("resize", measureWidth);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener("resize", measureWidth);
    };
  }, [open, measureWidth]);

  const checkBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distance <= BOTTOM_THRESHOLD) setReachedBottom(true);
  }, []);

  // Once every page has rendered, if the whole document already fits without
  // scrolling there is nothing to scroll — treat that as "read to the end" so
  // the gate can never get permanently stuck on a short document.
  useEffect(() => {
    if (numPages > 0 && renderedCount >= numPages) {
      const id = window.setTimeout(() => {
        const el = scrollRef.current;
        if (el && el.scrollHeight - el.clientHeight <= BOTTOM_THRESHOLD) {
          setReachedBottom(true);
        }
      }, 150);
      return () => window.clearTimeout(id);
    }
  }, [numPages, renderedCount]);

  return (
    <PopUp
      open={open}
      handleClose={onClose}
      heading="Guidelines"
      closeOnBackDrop={false}
      actionLeft={{ label: "Cancel", onClick: onClose }}
      actionRight={{
        label: "Acknowledged",
        disabled: !reachedBottom,
      }}
      handleRightBtnClick={onAcknowledge}
      customStyles={{
        wrapper: { maxWidth: "900px", width: "92vw" },
        actionButtons: { gap: "12px" },
      }}
    >
      <Box sx={{ width: "100%" }}>
        <Typography sx={{ fontWeight: 600, fontSize: 18, mb: 0.5 }}>
          Guidelines for One-time Upload of Revised KML Files
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Please read the complete document. Scroll to the end to enable
          acknowledgement.
        </Typography>

        <Box
          ref={scrollRef}
          onScroll={checkBottom}
          sx={{
            height: "60vh",
            overflowY: "auto",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            backgroundColor: "#f5f5f5",
            p: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {loadError ? (
            <Typography color="error" sx={{ py: 4 }}>
              Unable to load the guidelines document. Please try again.
            </Typography>
          ) : (
            <Document
              file={GUIDELINES_PDF}
              onLoadSuccess={(pdf) => setNumPages(pdf.numPages)}
              onLoadError={() => setLoadError(true)}
              loading={
                <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
                  <CircularProgress />
                </Box>
              }
            >
              {Array.from({ length: numPages }, (_, i) => (
                <Box
                  key={`page_${i + 1}`}
                  sx={{ mb: 2, boxShadow: 2, backgroundColor: "#fff" }}
                >
                  <Page
                    pageNumber={i + 1}
                    width={pageWidth}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    onRenderSuccess={() => setRenderedCount((c) => c + 1)}
                  />
                </Box>
              ))}
            </Document>
          )}
        </Box>

        <Box
          sx={{
            mt: 1,
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            minHeight: 22,
          }}
        >
          {reachedBottom ? (
            <>
              <CheckCircleOutlineIcon color="success" fontSize="small" />
              <Typography variant="caption" color="success.main">
                You have reached the end. You can now acknowledge and continue.
              </Typography>
            </>
          ) : (
            !loadError && (
              <Typography
                color="error"
                sx={{ fontWeight: 700, fontSize: "0.95rem" }}
              >
                Scroll to the bottom of the document to continue.
              </Typography>
            )
          )}
        </Box>
      </Box>
    </PopUp>
  );
};

export default AckGuidelinesModal;
